// Redux-toolkit
import { FC } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import startCase from 'lodash/startCase';
import { TDialogAction, BaseDialog } from '@mgm/common/components/base';
import { TAssignOrder, TCommissionPlan, TMember } from '../../../../types';
import { useUpdateMemberAPI } from '../../../../hooks/api';
import { BASE_LOCATION, DEFAULT_CURRENCY } from '../../../../data';

type TReassignOrderDialog = {
  isOpenDialog: boolean,
  setIsOpenDialog: (val: boolean) => void
  updatedOrder?: TAssignOrder
};

const AssignShopifyIdDialog: FC<TReassignOrderDialog> = ({
  isOpenDialog,
  setIsOpenDialog,
  updatedOrder,
}) => {
  const { mutate: editMutate, isLoading: isUpdateMemberLoading } = useUpdateMemberAPI();

  const { t } = useTranslation(['tasklists', 'common', 'orders']);

  const handleCloseDialog = async () => {
    setIsOpenDialog(false);
  };

  const updateMember = () => {
    const editedMember = {
      name: updatedOrder?.member?.name || '',
      email: updatedOrder?.member?.email || '',
      permissionLevel: updatedOrder?.member?.permissionLevel as TMember['permissionLevel'],
      paymentType: updatedOrder?.member?.payment.paymentType || '',
      paymentAmount: Number(updatedOrder?.member?.payment?.paymentAmount),
      paymentCurrencyId: updatedOrder?.member?.payment?.paymentCurrencyId || DEFAULT_CURRENCY.id,
      department: updatedOrder?.member?.department || '',
      position: updatedOrder?.member?.position || '',
      phone: {
        e164: updatedOrder?.member?.phone?.e164 || '',
      },
      locations: updatedOrder?.member?.locations || BASE_LOCATION,
      shopifyId: updatedOrder?.userId || '',
      timeoffLimit: updatedOrder?.member?.timeoffLimit || 0,
      sickLeaveLimit: updatedOrder?.member?.sickLeaveLimit || 0,
      pinCode: updatedOrder?.member?.pinCode || '',
      commissionPlan: updatedOrder?.member?.commissionPlan as TCommissionPlan,
      clockIn: updatedOrder?.member?.clockIn as TMember['clockIn'],
      commissionTags: updatedOrder?.member?.commissionTags || [],
      isPromoterCommission: updatedOrder?.member?.isPromoterCommission || false,
    };

    editMutate(editedMember);
    setIsOpenDialog(false);
  };

  const dialogActions: Array<TDialogAction> = [
    {
      label: t('cancel', { ns: 'common' }),
      variant: 'outlined',
      color: 'primary',
      isDisabled: isUpdateMemberLoading,
      onClick: () => handleCloseDialog(),
    },
    {
      label: t('assign'),
      color: 'primary',
      isLoading: isUpdateMemberLoading,
      onClick: () => updateMember(),
    },
  ];

  return (
    <BaseDialog
      dialogWidth={450}
      dialogContent={(
        <Stack spacing={2}>
          <Typography variant="body2">
            {t('wouldYouLikeToAssign', {
              ns: 'orders', userId: updatedOrder?.userId, userName: startCase(updatedOrder?.member?.name), email: updatedOrder?.member?.email,
            })}
          </Typography>
          <Typography color="primary" variant="body2">
            {t('thisWillAutomatically', { ns: 'orders', userName: startCase(updatedOrder?.member?.name) })}
          </Typography>
        </Stack>
      )}
      isOpenDialog={isOpenDialog}
      dialogTitle={t('setupTeamMember', { ns: 'orders' })}
      handleClose={handleCloseDialog}
      dialogActions={dialogActions}
    />
  );
};

export {
  AssignShopifyIdDialog,
};
